/**
 * Every runtime AI operation in the product, with its default models and the
 * modes the admin console may set. The API Worker resolves each call's models
 * from this registry plus the saved `ai_operation_settings` row; the admin
 * console's Settings › AI page renders one row per entry.
 *
 * Claude Haiku 5.5 and Sonnet 5.5 carry the product (owner 2026-11-02), each
 * operation on whichever measured better on production documents and the
 * labelled fixtures: fixtures/model-matrix-2026-11-02.json in
 * `apps/api/src/ai/__tests__`. grok is retired (`isRetiredModel`).
 */
export type AiOperationKind = "structured" | "stream" | "vision" | "embedding";

export type AiOperationArea =
  | "Onboarding"
  | "Formation"
  | "Federal tax"
  | "Bookkeeping"
  | "Fax"
  | "State filings"
  | "Support"
  | "Website"
  | "Operator";

export type AiOperationWorker =
  "api" | "state-registry" | "support" | "edge" | "operator";

export type AiOperationMode = "on" | "off" | "shadow" | "enforce";

export interface AiOperationDefinition {
  readonly id: string;
  readonly label: string;
  readonly area: AiOperationArea;
  readonly worker: AiOperationWorker;
  /** What starts a call, in plain English. */
  readonly trigger: string;
  readonly description: string;
  readonly kind: AiOperationKind;
  /** Allowed modes. `['on']` means always on: the page shows no toggle. */
  readonly modes: readonly AiOperationMode[];
  readonly defaultMode: AiOperationMode;
  readonly defaultPrimary: string;
  /** `null` only where a second model makes no sense (embeddings). */
  readonly defaultFallback: string | null;
  /** False when the model is fixed by something else (an index's dimensions). */
  readonly modelsEditable: boolean;
  /**
   * The operation sends a PDF. Only CLIProxyAPI's models read one here
   * (`modelAllowedFor`): the gateway's `/compat` route cannot carry a PDF, and
   * measured 2026-11-02 Anthropic through it answered empty fields as a success.
   */
  readonly readsPdf?: boolean;
  /** Why the models cannot be changed, as staff read it. Set where `modelsEditable` is false. */
  readonly fixedModelReason?: string;
  /**
   * Models tried, in order, after the editable fallback. Not editable on
   * Settings › AI (the console shows them read-only); a model equal to the
   * resolved primary, fallback or an earlier extra is skipped at resolve time.
   */
  readonly extraFallbacks?: readonly string[];
}

const ALWAYS_ON = ["on"] as const;
const OFF_ON = ["off", "on"] as const;

const HAIKU = "claude-haiku-5-5";
const SONNET = "claude-sonnet-5-5";
// Classification falls back to it: 94-97 top-1 against Haiku's 83-89 (measured 2026-11-02), and the
// second opinion leads on Haiku, so the two opinions stay on different models when Sonnet fails.
const GEMINI = "gemini-3.8-flash-high";
// The two description operations (measured 2026-11-02, 27 production inputs): their text reaches the
// customer as it streams. Flash-Lite wrote all of them, within the limit, in under a second. Sonnet
// through CLIProxyAPI answered as Claude Code on 6 of 108 calls (the proxy's identity), and Haiku called
// 13 of 48 readable websites UNKNOWN, so Haiku only stands behind Flash-Lite.
const FLASH_LITE = "gemini-3.5-flash-lite";
// Support and Website (measured 2026-11-02, real tickets and scenarios, all with reasoning_effort
// "none"): email spam, Haiku caught 44 of 46 with no false alarm against Flash-Lite's 39. Chat spam,
// Haiku tagged 3 of 103 real chats, so Flash-Lite keeps it (43 of 46, none). Chat replies, gpt-6-luna
// passed 11 of 13 scenarios against 8 for either Claude. Action search, Flash-Lite 58 of 65, Sonnet 56,
// Haiku 38. Ticket agents stay on Sonnet: Haiku put a price in a draft that should have escalated.
// The support agents' metered fallback (owner 2026-10-23, NT-84): Workers AI tool-calling models,
// routed through the same AI Gateway. Drafts take the stronger one, the Desk the cheaper one.
const AGENT_FALLBACK = "@cf/moonshotai/kimi-k2.6";
const DESK_FALLBACK = "@cf/zai-org/glm-5.3-flash";

/**
 * Workers AI chat models the API Worker may call, all with function calling.
 * Verified 2026-10-23 against the live catalog (`function_calling: true`) and
 * a tool call through the gateway's `workers-ai` path. Settings › AI offers
 * these next to the CLIProxyAPI list.
 */
export const WORKERS_AI_CHAT_MODELS = [AGENT_FALLBACK, DESK_FALLBACK] as const;

/** A Workers AI catalog id (`@cf/…`): billed per token, routed to the gateway's `workers-ai` path. */
export function isWorkersAiModel(model: string): boolean {
  return model.startsWith("@cf/");
}

/**
 * Who serves a model id: `@cf/…` is Workers AI, `anthropic/…` is Anthropic
 * through the gateway's stored key (BYOK), anything else is CLIProxyAPI.
 */
export type AiProvider = "cliproxy" | "anthropic" | "workers-ai";

export const AI_PROVIDER_LABELS: Readonly<Record<AiProvider, string>> = {
  cliproxy: "CLIProxyAPI",
  anthropic: "Anthropic",
  "workers-ai": "Workers AI",
};

export function aiProviderOf(model: string): AiProvider {
  if (isWorkersAiModel(model)) return "workers-ai";
  if (model.startsWith("anthropic/")) return "anthropic";
  return "cliproxy";
}

// The CLIProxyAPI outage links (owner 2026-10-24, NT-91): CLIProxyAPI runs on the owner's
// personal subscriptions, so every chat operation ends on Anthropic (the key stored on the
// gateway, billed to the API account) and then a Workers AI model. Verified 2026-10-24 through
// the gateway's `/compat/chat/completions` with the stored key.
const ANTHROPIC_FAST = "anthropic/claude-haiku-5-5";
const ANTHROPIC_STRONG = "anthropic/claude-sonnet-5-5";
/** Anthropic chat models the gateway's stored key serves; Settings › AI offers them next to CLIProxyAPI's list. */
export const ANTHROPIC_CHAT_MODELS = [
  ANTHROPIC_FAST,
  ANTHROPIC_STRONG,
] as const;
/** Structured answers: the stronger Workers AI model. */
const OUTAGE_SMART = [ANTHROPIC_FAST, AGENT_FALLBACK] as const;
/** Short, fast answers: the cheaper Workers AI model. */
const OUTAGE_FAST = [ANTHROPIC_FAST, DESK_FALLBACK] as const;

// Reading a PDF (measured 2026-11-02 on production documents): Haiku and Sonnet through CLIProxyAPI
// matched the stored EIN, state, date and filing id on every readable document, at 2-3 s. Through the
// gateway's `/compat` route the same Haiku answered empty fields as a success and Sonnet was refused,
// so no Anthropic link follows: in a CLIProxyAPI outage these fail and page rather than store a blank.
const PDF_LAST = [GEMINI] as const;

// The captcha chain (owner 2026-10-30): images go to Anthropic or another CLIProxyAPI model that
// reads them, never GLM. Haiku reads a six-character captcha in a few seconds, so it leads there. The
// Workers AI link is dropped: no Workers AI model is verified here to read an image.
const VISION_LAST = [ANTHROPIC_STRONG] as const;

const RETIRED_REASON = "grok models are retired";

/** grok (owner 2026-11-02): no operation runs it, whatever a stored row or a caller names. */
export function isRetiredModel(model: string): boolean {
  return /(^|\/)grok-/i.test(model);
}

/**
 * Whether Settings › AI may offer a served id. CLIProxyAPI's list also carries
 * retired models and ones that answer no chat call (image, voice, speech).
 */
export function isSelectableModel(model: string): boolean {
  return !isRetiredModel(model) && !/image|imagine|tts|voice/i.test(model);
}

/**
 * Why `model` may not read images for us, with the reason staff read, or null
 * when it may (owner 2026-10-30). GLM (`glm` anywhere in the id, any provider):
 * cannot read images.
 */
export function visionRefusalReason(model: string): string | null {
  if (isRetiredModel(model)) return RETIRED_REASON;
  return /glm/i.test(model) ? "GLM models cannot read images" : null;
}

/** False for a model no vision operation may use (grok, any GLM). */
export function isVisionCapable(model: string): boolean {
  return visionRefusalReason(model) === null;
}

/**
 * The operations that may run Sonnet on the Anthropic route: the agents call
 * tools, web search uses Anthropic's native path, and the queued ticket draft
 * (`support_ticket_draft`, `TICKET_DRAFT_RESPONSE_FORMAT`) sends a schema Sonnet
 * answers (`refusesStructuredAnswers`), which the ledger shows working.
 */
const SONNET_ON_ANTHROPIC = new Set<string>([
  "support_ticket_draft",
  "support_ticket_sales_quote",
  "support_ticket_filing_status",
  "support_ticket_chat",
  "support_web_research",
  "support_desk",
]);

/**
 * Sonnet 5.5 on the Anthropic route refuses a forced tool choice, and the
 * gateway's `/compat` route forces one for a `json_schema` that Anthropic's own
 * structured output cannot take. Probed 2026-11-02: a schema with
 * `additionalProperties: false` and no number bounds answered 200; adding
 * `minimum`/`maximum`, or leaving out `additionalProperties`, answered 400,
 * "tool_choice: type tool and any are not supported for this model". Measured
 * 2026-11-02: 400 on every classification, PDF and email spam call. A 400 from
 * Anthropic ends the chain, so saving it would stop the operation outright.
 */
function refusesStructuredAnswers(
  op: Pick<AiOperationDefinition, "id" | "kind">,
  model: string,
): boolean {
  return (
    model === ANTHROPIC_STRONG &&
    op.kind === "structured" &&
    !SONNET_ON_ANTHROPIC.has(op.id)
  );
}

/** Why `op` may not run `model`, in a sentence's middle, or null when it may. */
export function modelRefusalReason(
  op: Pick<AiOperationDefinition, "id" | "kind" | "readsPdf">,
  model: string,
): string | null {
  if (isRetiredModel(model)) return RETIRED_REASON;
  if (op.readsPdf && aiProviderOf(model) !== "cliproxy") {
    return "only CLIProxyAPI's models can read a PDF here";
  }
  if (refusesStructuredAnswers(op, model)) {
    return "Anthropic's API refuses this operation's structured answer on Sonnet";
  }
  return op.kind === "vision" ? visionRefusalReason(model) : null;
}

/** The one rule every chain and every save goes through. */
export function modelAllowedFor(
  op: Pick<AiOperationDefinition, "id" | "kind" | "readsPdf">,
  model: string,
): boolean {
  return modelRefusalReason(op, model) === null;
}

export type AiReasoningEffort = "none" | "minimal" | "low" | "medium";

/**
 * Models on CLIProxyAPI that reason before they answer and accept
 * `reasoning_effort`. Any other model is sent no effort at all (its handling is
 * unverified, so it is not guessed). The Anthropic route takes none either: a
 * structured request carrying it answered 400 (measured 2026-11-02), and Haiku
 * there fit the spam judge's 200-token cap 20 times of 20 without it.
 */
export const FAST_REASONING_EFFORT: Readonly<
  Record<string, AiReasoningEffort>
> = {
  "gemini-3.8-flash-high": "none",
  // Ten classify lines take ~4 s with it, 8-9 s (sometimes past 15 s) without; accuracy is the same.
  // At the spam judge's 200-token cap, 74-85% of answers stopped at finish=length without it.
  [HAIKU]: "none",
  // Same on Sonnet, measured 2026-11-02: ten lines in ~5 s with it, 7-10 s without; accuracy held.
  [SONNET]: "none",
};

// The TicketAgent operations (owner decision 2026-10-27): Claude Sonnet 5.5 through the gateway's
// stored key is the primary (metered, but it reasons and follows a long tool-using prompt; gpt-6-luna
// answered T-00387 with no reasoning at all), gpt-6-luna on CLIProxyAPI is the fallback, Kimi on
// Workers AI the last link. A repeat of a resolved model is skipped (`resolveOperationSetting`).
const TICKET_AGENT_MODELS = {
  defaultPrimary: ANTHROPIC_STRONG,
  defaultFallback: "gpt-6-luna",
  extraFallbacks: [AGENT_FALLBACK],
} as const;

export const AI_OPERATIONS = [
  {
    id: "naics_determination",
    label: "NAICS code suggestion",
    area: "Onboarding",
    worker: "api",
    trigger: "A customer describes their business during onboarding",
    description:
      "Suggests the NAICS code for the company from its description.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: SONNET,
    defaultFallback: HAIKU,
    extraFallbacks: OUTAGE_SMART,
    modelsEditable: true,
  },
  {
    id: "company_description_from_site",
    label: "Company description from website",
    area: "Onboarding",
    worker: "api",
    trigger: "A customer enters their website on the Company Information step",
    description:
      "Drafts a one-line company description from the website, streamed as it is written.",
    kind: "stream",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: FLASH_LITE,
    defaultFallback: HAIKU,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "company_description_enhance",
    label: "Company description tidy-up",
    area: "Onboarding",
    worker: "api",
    trigger: "A customer asks to improve the description they typed",
    description:
      "Tidies the customer's own description, streamed as it is written.",
    kind: "stream",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: FLASH_LITE,
    defaultFallback: HAIKU,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "address_extraction",
    label: "Address extraction from PDF",
    area: "Onboarding",
    worker: "api",
    trigger: "A customer uploads a document to fill in addresses",
    description: "Reads addresses out of an uploaded PDF.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    readsPdf: true,
    defaultPrimary: HAIKU,
    defaultFallback: SONNET,
    extraFallbacks: PDF_LAST,
    modelsEditable: true,
  },
  {
    id: "ein_extraction",
    label: "EIN extraction from PDF",
    area: "Onboarding",
    worker: "api",
    trigger: "A customer uploads their EIN letter",
    description: "Reads the EIN out of an uploaded PDF.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    readsPdf: true,
    defaultPrimary: HAIKU,
    defaultFallback: SONNET,
    extraFallbacks: PDF_LAST,
    modelsEditable: true,
  },
  {
    id: "ss4_signed_check",
    label: "SS-4 signature check",
    area: "Formation",
    worker: "api",
    trigger: "A customer uploads a signed SS-4, or staff re-run the check",
    description:
      "Checks that an uploaded SS-4 is really signed before staff review it.",
    kind: "vision",
    modes: ALWAYS_ON,
    defaultMode: "on",
    readsPdf: true,
    defaultPrimary: SONNET,
    defaultFallback: HAIKU,
    extraFallbacks: PDF_LAST,
    modelsEditable: true,
  },
  {
    id: "transaction_classification",
    label: "Transaction account suggestion",
    area: "Bookkeeping",
    worker: "api",
    trigger: "New bank or Stripe lines arrive, or staff classify a line",
    description:
      "Suggests three ledger accounts for a transaction. A human always confirms.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: SONNET,
    defaultFallback: GEMINI,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "transaction_second_opinion",
    label: "Transaction second opinion",
    area: "Bookkeeping",
    worker: "api",
    trigger: "After the account suggestion, for the same lines",
    description:
      "A second model checks the suggested account; disagreement sends the line to review.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "off",
    defaultPrimary: HAIKU,
    defaultFallback: GEMINI,
    extraFallbacks: OUTAGE_SMART,
    modelsEditable: true,
  },
  {
    id: "transaction_import_question",
    label: "Transaction import question",
    area: "Bookkeeping",
    worker: "api",
    trigger: "A line arrives without a bank category",
    description:
      "Picks which question to ask the customer about a line (where it went, who sent it, business or personal).",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "off",
    defaultPrimary: HAIKU,
    defaultFallback: SONNET,
    extraFallbacks: OUTAGE_SMART,
    modelsEditable: true,
  },
  {
    id: "inbound_fax_attribution",
    label: "Inbound fax matching",
    area: "Fax",
    worker: "api",
    trigger: "A fax arrives, or staff re-run the match",
    description: "Matches a received fax to its company and order.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    readsPdf: true,
    defaultPrimary: HAIKU,
    defaultFallback: SONNET,
    extraFallbacks: PDF_LAST,
    modelsEditable: true,
  },
  {
    id: "registry_captcha",
    label: "State portal captcha",
    area: "State filings",
    worker: "state-registry",
    trigger: "An automated Wyoming or Delaware registry run meets a captcha",
    description: "Reads the captcha image on the state registry portal.",
    kind: "vision",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: ANTHROPIC_FAST,
    defaultFallback: GEMINI,
    extraFallbacks: VISION_LAST,
    modelsEditable: true,
  },
  {
    id: "chat_reply",
    label: "Live chat reply",
    area: "Support",
    worker: "support",
    trigger: "A visitor sends a live chat message",
    description:
      "Writes the chat reply or draft. How much the AI may do is set in Support › Settings › AI.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: "gpt-6-luna",
    defaultFallback: HAIKU,
    extraFallbacks: OUTAGE_SMART,
    modelsEditable: true,
  },
  {
    id: "chat_spam_judge",
    label: "Live chat spam tag",
    area: "Support",
    worker: "support",
    trigger: "A visitor's first chat message",
    description: "Tags a new chat as suspected spam.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "off",
    defaultPrimary: FLASH_LITE,
    defaultFallback: HAIKU,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "chat_triage",
    label: "Live chat triage",
    area: "Support",
    worker: "support",
    trigger: "Each chat message, before the reply",
    description:
      "Sorts the message (acknowledgement, answerable, needs a human). Shadow records the verdict only; enforce may skip the reply model.",
    kind: "structured",
    modes: ["off", "shadow", "enforce"],
    defaultMode: "off",
    defaultPrimary: HAIKU,
    defaultFallback: SONNET,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "support_ticket_draft",
    label: "Email and form ticket draft",
    area: "Support",
    worker: "support",
    trigger: "A customer's email or contact form message lands in the inbox",
    description:
      "Suggests tags and a reply from the knowledge base. Shadow records the run only; enforce also adds the draft to the ticket as a private note. Nothing is sent.",
    kind: "structured",
    modes: ["off", "shadow", "enforce"],
    defaultMode: "off",
    ...TICKET_AGENT_MODELS,
    modelsEditable: true,
  },
  {
    id: "support_ticket_sales_quote",
    label: "Ticket draft: sales quote profile",
    area: "Support",
    worker: "support",
    trigger:
      "Staff ask the TicketAgent for a sales_quote draft on an email or form ticket",
    description:
      "Qualifies a price question and may save a draft quote priced from the pricing reference. A person approves and sends every quote; nothing is sent.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "on",
    ...TICKET_AGENT_MODELS,
    modelsEditable: true,
  },
  {
    id: "support_ticket_filing_status",
    label: "Ticket draft: filing status profile",
    area: "Support",
    worker: "support",
    trigger:
      "Staff ask the TicketAgent for a filing_status draft on an email or form ticket",
    description:
      "Reads the customer's filing state and drafts a status reply. A person approves every send; nothing is sent.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "on",
    ...TICKET_AGENT_MODELS,
    modelsEditable: true,
  },
  {
    id: "support_ticket_chat",
    label: "Ticket chat with its agent",
    area: "Support",
    worker: "support",
    trigger:
      "Staff write in an email or form ticket's Telegram topic or the Mini App Chat tab",
    description:
      "Answers staff about one ticket with read tools, drafts or revises a reply and proposes solve, spam or assign. A person sends and confirms everything. Off stops ticket chat at once.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "on",
    ...TICKET_AGENT_MODELS,
    modelsEditable: true,
  },
  {
    // Anthropic only: the web search tool and its citations exist on its own API. Models are fixed,
    // because a saved CLIProxyAPI model could not run it. Haiku reads the long search results cheaply.
    id: "support_web_research",
    label: "Ticket agent web research",
    area: "Support",
    worker: "support",
    trigger:
      "The ticket's agent looks up a rule or an outside platform on the web",
    description:
      "Answers one question with Anthropic's web search tool, at most 4 searches a call, each billed on top of the tokens. No customer data goes in. Off stops research at once; the agent carries on without it.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "on",
    defaultPrimary: ANTHROPIC_FAST,
    defaultFallback: ANTHROPIC_STRONG,
    modelsEditable: false,
    fixedModelReason:
      "The models are fixed: web search with citations runs only on Anthropic's own API.",
  },
  {
    id: "support_desk",
    label: "Support desk assistant",
    area: "Support",
    worker: "support",
    trigger:
      "Staff write to the Desk in Telegram, or a scheduled inbox sweep runs",
    description:
      "Reads the inbox with tools and proposes what staff should do next. It never sends, solves or marks anything itself.",
    kind: "structured",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: HAIKU,
    defaultFallback: SONNET,
    extraFallbacks: [ANTHROPIC_STRONG, DESK_FALLBACK],
    modelsEditable: true,
  },
  {
    id: "knowledge_embedding",
    label: "Knowledge base search",
    area: "Support",
    worker: "support",
    trigger: "Chat and staff searches, and knowledge base imports and edits",
    description:
      "Embeds questions and knowledge sections for search. Fixed by the search index, so it cannot be changed here.",
    kind: "embedding",
    modes: ALWAYS_ON,
    defaultMode: "on",
    defaultPrimary: "@cf/google/embeddinggemma-300m",
    defaultFallback: null,
    modelsEditable: false,
    fixedModelReason:
      "The model is fixed: the knowledge search index is built for it.",
  },
  {
    id: "contact_spam_judge",
    label: "Contact form spam tag",
    area: "Website",
    worker: "edge",
    trigger: "A visitor submits a form on nonresident.tax",
    description: "Tags a contact form submission as suspected spam.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "off",
    defaultPrimary: HAIKU,
    defaultFallback: FLASH_LITE,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "email_spam_judge",
    label: "Support email spam tag",
    area: "Support",
    worker: "support",
    trigger: "A new email to support@ from someone who is not a customer",
    description:
      "Tags a new email ticket as suspected spam or a vendor pitch, and an automated sender's other mail no-reply-needed; government senders are tagged no-reply-needed without a call.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "off",
    defaultPrimary: HAIKU,
    defaultFallback: FLASH_LITE,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
  {
    id: "operator_search_rerank",
    label: "Operator action search",
    area: "Operator",
    worker: "operator",
    trigger: "An operator agent searches the admin actions",
    description:
      "Ranks admin actions by relevance; plain text matching is used when off or on failure.",
    kind: "structured",
    modes: OFF_ON,
    defaultMode: "off",
    defaultPrimary: FLASH_LITE,
    defaultFallback: SONNET,
    extraFallbacks: OUTAGE_FAST,
    modelsEditable: true,
  },
] as const satisfies readonly AiOperationDefinition[];

export type AiOperationId = (typeof AI_OPERATIONS)[number]["id"];

const BY_ID = new Map<string, AiOperationDefinition>(
  AI_OPERATIONS.map((op) => [op.id, op]),
);

export function aiOperation(id: AiOperationId): AiOperationDefinition {
  const op = BY_ID.get(id);
  if (!op) throw new Error(`Unknown AI operation: ${id}`);
  return op;
}

export function isAiOperationId(value: string): value is AiOperationId {
  return BY_ID.has(value);
}

/** Support, edge and operator call models over `AI_SERVICE`; the API and registry Workers run their own. */
export function servedOverAiService(op: AiOperationDefinition): boolean {
  return (
    op.worker === "support" || op.worker === "edge" || op.worker === "operator"
  );
}
